// „Neue Bestellung“ für den Einkauf (Jasmin): rotes Zähler-Badge im Menü, NEU-Etikett
// bis zum Anklicken und ein lustiges Konfetti-Popup, wenn etwas Neues eintrifft.
// Die Daten kommen von /api/einkauf-neu (nur für Einkauf-Empfänger „aktiv“); Abfrage alle 30 s
// und sobald das Fenster wieder sichtbar wird.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type EinkaufNeuDaten } from '../lib/api';

const LEER: EinkaufNeuDaten = { aktiv: false, vorname: null, aufgaben: [], banf: [], bedarf: [], sonstiges: [] };
const POLL_MS = 10_000;          // Empfänger (Jasmin): fast live
const POLL_LANGSAM_MS = 300_000;  // alle anderen: nur gelegentlich nachsehen

type Ctx = {
  daten: EinkaufNeuDaten;
  /** Aufgaben-IDs bzw. BANF-(Zettel-)IDs als gesehen markieren → NEU verschwindet sofort */
  gesehen: (art: 'aufgabe' | 'banf' | 'bedarf' | 'sonstiges', ids: number[]) => void;
  reload: () => void;
};
const EinkaufNeuContext = createContext<Ctx>({ daten: LEER, gesehen: () => {}, reload: () => {} });
export const useEinkaufNeu = () => useContext(EinkaufNeuContext);

/** Anzahl für den Menüpunkt: Einkauf-Dashboard = neue Aufgaben, BANF = BANFs mit neuen Positionen */
export function neuAnzahl(d: EinkaufNeuDaten, pfad: string): number {
  if (pfad === '/einkauf') return d.aufgaben.length;
  if (pfad === '/banf') return new Set(d.banf.map(p => p.zettel_id)).size;
  if (pfad === '/blechbedarf') return d.bedarf.length;
  if (pfad === '/einkauf-anfragen') return d.sonstiges.length;
  return 0;
}

export function EinkaufNeuProvider({ aktiv, children }: { aktiv: boolean; children: ReactNode }) {
  const [daten, setDaten] = useState<EinkaufNeuDaten>(LEER);
  const geschlossen = useRef(false);
  const aktivRef = useRef(false);

  const laden = useCallback(async () => {
    if (!aktiv) return;
    try {
      const d = await api.einkaufNeu.list();
      if (d) aktivRef.current = !!d.aktiv;
      if (!geschlossen.current && d && Array.isArray(d.aufgaben) && Array.isArray(d.banf)) setDaten({ ...d, bedarf: Array.isArray(d.bedarf) ? d.bedarf : [], sonstiges: Array.isArray(d.sonstiges) ? d.sonstiges : [] });
    } catch { /* offline / nicht angemeldet: Badge bleibt wie es ist */ }
  }, [aktiv]);

  useEffect(() => {
    geschlossen.current = false;
    if (!aktiv) return () => { geschlossen.current = true; };
    // Abfrage-Schleife: auch bei verstecktem Tab (Jasmin arbeitet oft in anderen Fenstern) — das Popup wartet dann auf Sichtbarkeit
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schleife = () => {
      timer = setTimeout(async () => { await laden(); if (!geschlossen.current) schleife(); }, aktivRef.current ? POLL_MS : POLL_LANGSAM_MS);
    };
    void laden().finally(() => { if (!geschlossen.current) schleife(); });
    const vis = () => { if (document.visibilityState === 'visible') void laden(); };
    document.addEventListener('visibilitychange', vis);
    window.addEventListener('focus', vis);
    window.addEventListener('online', vis);
    return () => { geschlossen.current = true; if (timer) clearTimeout(timer); document.removeEventListener('visibilitychange', vis); window.removeEventListener('focus', vis); window.removeEventListener('online', vis); };
  }, [aktiv, laden]);

  // Tab-Titel: „(2) Sickinger Einkauf“ — auch bei verstecktem Tab sichtbar
  const gesamt = daten.aufgaben.length + new Set(daten.banf.map(p => p.zettel_id)).size + daten.bedarf.length + daten.sonstiges.length;
  useEffect(() => {
    if (!aktiv || typeof document === 'undefined') return;
    const basis = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = gesamt > 0 ? `(${gesamt}) ${basis}` : basis;
  }, [aktiv, gesamt]);

  const gesehen = useCallback((art: 'aufgabe' | 'banf' | 'bedarf' | 'sonstiges', ids: number[]) => {
    if (ids.length === 0) return;
    // sofort aus der Anzeige nehmen, dann beim Server festhalten
    setDaten(d => ({
      ...d,
      aufgaben: art === 'aufgabe' ? d.aufgaben.filter(a => !ids.includes(a.id)) : d.aufgaben,
      banf: art === 'banf' ? d.banf.filter(p => !ids.includes(p.zettel_id)) : d.banf,
      bedarf: art === 'bedarf' ? d.bedarf.filter(b => !ids.includes(b.id)) : d.bedarf,
      sonstiges: art === 'sonstiges' ? d.sonstiges.filter(s => !ids.includes(s.id)) : d.sonstiges,
    }));
    api.einkaufNeu.gesehen(art, ids).catch(() => { void laden(); });
  }, [laden]);

  const wert = useMemo(() => ({ daten, gesehen, reload: () => { void laden(); } }), [daten, gesehen, laden]);
  return (
    <EinkaufNeuContext.Provider value={wert}>
      {children}
      {aktiv && <NeuPopup />}
    </EinkaufNeuContext.Provider>
  );
}

/** Kleines rotes „NEU“-Etikett (bleibt, bis Jasmin die Zeile anklickt) */
export function NeuEtikett({ klein = false }: { klein?: boolean }) {
  return (
    <span data-testid="neu-etikett"
          className={`inline-block bg-red-500 text-white font-bold rounded ${klein ? 'text-[9px] px-1 py-px' : 'text-[10px] px-1.5 py-0.5'} tracking-wide`}>
      NEU
    </span>
  );
}

/** Rotes Zähler-Badge im Menü (wie die Zahl am Handy-App-Symbol) */
export function ZaehlerBadge({ n }: { n: number }) {
  if (n <= 0) return null;
  return (
    <span data-testid="menue-badge" aria-label={`${n} neu`}
          className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
      {n > 99 ? '99+' : n}
    </span>
  );
}

/* ---------- Animationen (Popup + Dashboard-Banner) ---------- */

const NEU_CSS = `
@keyframes neuGradient { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
@keyframes neuPuls { 0%, 100% { box-shadow: 0 0 0 0 rgba(236,72,153,.55), 0 10px 40px rgba(0,0,0,.25); } 50% { box-shadow: 0 0 0 16px rgba(236,72,153,0), 0 10px 40px rgba(0,0,0,.25); } }
@keyframes neuSchweben { 0%, 100% { transform: translateY(0) rotate(-1deg); } 50% { transform: translateY(-8px) rotate(1deg); } }
@keyframes neuWackeln { 0%, 100% { transform: rotate(-8deg) scale(1); } 50% { transform: rotate(8deg) scale(1.15); } }
@keyframes neuGlocke { 0%, 60%, 100% { transform: rotate(0); } 10% { transform: rotate(18deg); } 20% { transform: rotate(-16deg); } 30% { transform: rotate(12deg); } 40% { transform: rotate(-8deg); } 50% { transform: rotate(4deg); } }
@media (prefers-reduced-motion: reduce) { .neu-anim { animation: none !important; } }
`;
const REGENBOGEN = 'linear-gradient(120deg, #ec4899, #f59e0b, #10b981, #3b82f6, #8b5cf6, #ec4899)';
const REGENBOGEN_TRANSPARENT = 'linear-gradient(120deg, rgba(236,72,153,.88), rgba(245,158,11,.88), rgba(16,185,129,.88), rgba(59,130,246,.88), rgba(139,92,246,.88), rgba(236,72,153,.88))';

/** Auffälliger, bunt animierter Block oben im Einkauf-Dashboard: alle neuen Bestellungen, live (Abfrage alle 10 s). */
export function NeuBanner() {
  const { daten, gesehen } = useEinkaufNeu();
  const navigate = useNavigate();
  let eintraege = neuEintraege(daten);
  let vorschau = false;
  try { vorschau = new URLSearchParams(window.location.search).get('neu-test') === '1'; } catch { /* egal */ }
  if (vorschau) eintraege = [{ key: 'test', ziel: '/einkauf', art: 'aufgabe', ids: [], text: '🔵 Bestellung: 10x Muffe 1/2 Zoll (Vorschau — so sieht eine neue Bestellung aus)' }, ...eintraege];
  if (eintraege.length === 0) return null;
  const gesamt = eintraege.length;
  return (
    <section data-testid="neu-banner" aria-live="polite"
             className="neu-anim mb-6 rounded-2xl p-1 text-white"
             style={{ backgroundImage: REGENBOGEN, backgroundSize: '300% 300%', animation: 'neuGradient 6s ease infinite, neuPuls 2s ease-in-out infinite' }}>
      <style>{NEU_CSS}</style>
      <div className="rounded-[14px] bg-black/15 px-4 py-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="neu-anim text-3xl inline-block" style={{ animation: 'neuGlocke 2.4s ease-in-out infinite', transformOrigin: '50% 10%' }} aria-hidden>🔔</span>
          <h2 className="text-lg font-extrabold tracking-tight drop-shadow">
            {gesamt === 1 ? 'Neue Bestellung!' : `${gesamt} neue Bestellungen!`}
          </h2>
          <span className="text-xs opacity-90">live · prüft alle 10 Sekunden</span>
          <button data-testid="neu-banner-alle" type="button"
                  onClick={() => { for (const e of eintraege) gesehen(e.art, e.ids); }}
                  className="ml-auto text-xs bg-white/25 hover:bg-white/40 rounded-lg px-3 py-1.5 font-semibold">
            ✓ Alle gesehen
          </button>
        </div>
        <ul className="mt-2 space-y-1.5">
          {eintraege.slice(0, 8).map(e => (
            <li key={e.key}>
              <button type="button" data-testid="neu-banner-zeile"
                      onClick={() => { gesehen(e.art, e.ids); navigate(e.ziel); }}
                      className="w-full text-left bg-white/90 hover:bg-white text-slate-800 rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-2 shadow-sm">
                <span className="flex-1 min-w-0 truncate">{e.text}</span>
                <NeuEtikett klein />
                <span aria-hidden className="text-slate-400">→</span>
              </button>
            </li>
          ))}
          {eintraege.length > 8 && <li className="text-xs opacity-90 px-1">… und {eintraege.length - 8} weitere</li>}
        </ul>
      </div>
    </section>
  );
}

/* ---------- Popup mit Konfetti ---------- */

const SPRUECHE = [
  (n: string) => `📦 Ding Dong! ${n}, es hat geklingelt!`,
  (n: string) => `🚀 Houston, ${n} – wir haben eine Bestellung!`,
  (n: string) => `🎉 ${n}, frische Bestellung im Anmarsch – Ärmel hoch, Kaffee holen!`,
  (n: string) => `🦸 ${n}, der Einkauf ruft: Es gibt Neues zu bestellen!`,
  (n: string) => `🧙 Abrakadabra! Und schon liegt eine neue Bestellung da, ${n}.`,
  (n: string) => `🛒 Psst, ${n} … jemand braucht was. Wieder mal. 😄`,
  (n: string) => `🎈 Überraschung, ${n}! Die Werkstatt hat wieder Wünsche.`,
];
const POPUP_KEY = 'einkauf-neu-popup-gezeigt';
const TON_KEY = 'einkauf-neu-ton-aus';
const tonAn = (): boolean => { try { return localStorage.getItem(TON_KEY) !== '1'; } catch { return true; } };

/** Kurze Fanfare (3 Töne) per WebAudio — still, falls der Browser sie noch nicht erlaubt */
export function fanfare() {
  try {
    const AC = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext });
    const Ctor = AC.AudioContext ?? AC.webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();
    [[523.25, 0], [659.25, 0.13], [783.99, 0.26], [1046.5, 0.42]].forEach(([f, t]) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'triangle'; o.frequency.value = f as number;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + (t as number));
      g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + (t as number) + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + (t as number) + 0.35);
      o.connect(g); g.connect(ctx.destination); o.start(ctx.currentTime + (t as number)); o.stop(ctx.currentTime + (t as number) + 0.4);
    });
    setTimeout(() => { void ctx.close(); }, 1500);
  } catch { /* kein Ton */ }
}

function gezeigtLesen(): Set<string> {
  try { return new Set<string>(JSON.parse(localStorage.getItem(POPUP_KEY) ?? '[]')); } catch { return new Set(); }
}
function gezeigtSchreiben(s: Set<string>) {
  try { localStorage.setItem(POPUP_KEY, JSON.stringify([...s].slice(-400))); } catch { /* privat/blockiert */ }
}

type Zeile = { key: string; text: string; ziel: string };

/** Alle NEU-Einträge als anzeigbare Zeilen (Popup + Dashboard-Banner). `ausser` = Schlüssel, die schon angekündigt wurden. */
export type NeuEintrag = Zeile & { art: 'aufgabe' | 'banf' | 'bedarf' | 'sonstiges'; ids: number[] };
export function neuEintraege(daten: EinkaufNeuDaten, ausser?: Set<string>): NeuEintrag[] {
  const aus = ausser ?? new Set<string>();
  const neu: NeuEintrag[] = [];
  for (const a of daten.aufgaben) {
    const key = `a:${a.id}`;
    if (!aus.has(key)) neu.push({
      key, ziel: /^Gasbestellung/i.test(a.titel) ? '/gas' : '/einkauf', art: 'aufgabe', ids: [a.id],
      text: `${a.typ === 'anfrage' ? '🟣 Anfrage' : '🔵 Bestellung'}: ${a.titel}${a.von ? ` (von ${a.von})` : ''}`,
    });
  }
  for (const b of daten.bedarf) {
    const key = `d:${b.id}`;
    if (!aus.has(key)) neu.push({
      key, ziel: '/blechbedarf', art: 'bedarf', ids: [b.id],
      text: `${b.typ === 'anfrage' ? '🟣 Preis-Anfrage' : '🔵 Blechbedarf'}: ${[b.menge ? `${b.menge}×` : '', b.form, b.werkstoff, b.format, b.dicke ? `${b.dicke} mm` : ''].filter(Boolean).join(' ')}${b.referenz ? ` · ${b.referenz}` : ''}${b.sb ? ` (von ${b.sb})` : ''}`,
    });
  }
  for (const s of daten.sonstiges) {
    const key = `s:${s.id}`;
    if (!aus.has(key)) neu.push({
      key, ziel: '/einkauf-anfragen', art: 'sonstiges', ids: [s.id],
      text: `📦 ${s.kategorie === 'loxpal' ? 'Loxpal' : 'Weitere Anfrage'}: ${[s.stueck ? `${s.stueck}×` : '', s.was].filter(Boolean).join(' ')}${s.referenz ? ` · ${s.referenz}` : ''}${s.von ? ` (von ${s.von})` : ''}`,
    });
  }
  const jeZettel = new Map<number, { dv: string; n: number; keys: string[] }>();
  for (const p of daten.banf) {
    const key = `b:${p.id}`;
    if (aus.has(key)) continue;
    const z = jeZettel.get(p.zettel_id) ?? { dv: p.dv_nummer, n: 0, keys: [] };
    z.n += 1; z.keys.push(key); jeZettel.set(p.zettel_id, z);
  }
  for (const [zid, z] of jeZettel)
    neu.push({ key: z.keys.join('|'), ziel: `/banf/${zid}`, art: 'banf', ids: [zid],
               text: `📋 BANF-${String(zid).padStart(4, '0')} · ${z.dv}: ${z.n} neue Position${z.n === 1 ? '' : 'en'}` });
  return neu;
}

function NeuPopup() {
  const { daten } = useEinkaufNeu();
  const navigate = useNavigate();
  const [zeilen, setZeilen] = useState<Zeile[]>([]);
  const [spruch, setSpruch] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const [ton, setTon] = useState(tonAn());
  const [sichtbar, setSichtbar] = useState(typeof document === 'undefined' ? true : document.visibilityState === 'visible');

  useEffect(() => {
    const f = () => setSichtbar(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', f);
    return () => document.removeEventListener('visibilitychange', f);
  }, []);

  // Neues, das noch nie angekündigt wurde → Popup (nur bei sichtbarem Fenster)
  useEffect(() => {
    if (!sichtbar) return;
    const bekannt = gezeigtLesen();
    const neu: Zeile[] = neuEintraege(daten, bekannt);
    if (neu.length === 0) return;
    for (const z of neu) for (const k of z.key.split('|')) bekannt.add(k);
    gezeigtSchreiben(bekannt);
    setSpruch(Math.floor(Math.random() * SPRUECHE.length));
    setZeilen(alt => [...alt, ...neu]);
  }, [daten, sichtbar]);

  // Vorschau: …/einkauf?neu-test=1 zeigt das Popup mit einer Beispiel-Bestellung (zum Ausprobieren der Show)
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get('neu-test') === '1')
        setZeilen([{ key: 'test', ziel: '/einkauf', text: '🔵 Bestellung: 10x Muffe 1/2 Zoll (Test von Hamdi)' }]);
    } catch { /* egal */ }
  }, []);

  // Auftritt: Box „springt“ herein
  useEffect(() => {
    if (zeilen.length === 0) return;
    try {
      boxRef.current?.animate?.([
        { transform: 'scale(.4) rotate(-6deg)', opacity: 0 },
        { transform: 'scale(1.08) rotate(2deg)', opacity: 1, offset: 0.6 },
        { transform: 'scale(1) rotate(0)', opacity: 1 },
      ], { duration: 520, easing: 'cubic-bezier(.2,.9,.3,1.2)' });
    } catch { /* ältere Browser */ }
    if (tonAn()) fanfare();
  }, [zeilen.length === 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // weitere neue Einträge, während das Popup schon offen ist → noch eine Salve
  const anzahlRef = useRef(0);
  useEffect(() => {
    if (zeilen.length > anzahlRef.current && anzahlRef.current > 0) { konfetti(); if (tonAn()) fanfare(); }
    anzahlRef.current = zeilen.length;
  }, [zeilen.length]);

  useEffect(() => {
    if (zeilen.length === 0) return;
    const stop = konfetti();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setZeilen([]); };
    window.addEventListener('keydown', esc);
    return () => { stop(); window.removeEventListener('keydown', esc); };
  }, [zeilen.length === 0]); // eslint-disable-line react-hooks/exhaustive-deps

  if (zeilen.length === 0) return null;
  const name = daten.vorname || 'Chef';
  const zeigen = zeilen.slice(0, 5);
  return (
    <div data-testid="neu-popup" role="dialog" aria-label="Neue Bestellung"
         className="neu-anim fixed inset-0 z-[80] flex items-center justify-center p-4" onClick={() => setZeilen([])}
         style={{ backgroundImage: REGENBOGEN_TRANSPARENT, backgroundSize: '300% 300%', animation: 'neuGradient 5s ease infinite' }}>
      <style>{NEU_CSS}</style>
      <div ref={boxRef} className="neu-anim bg-white rounded-2xl w-full max-w-md overflow-hidden"
           style={{ animation: 'neuPuls 1.8s ease-in-out infinite' }} onClick={e => e.stopPropagation()}>
        <div className="neu-anim text-white px-6 py-5 text-center"
             style={{ backgroundImage: REGENBOGEN, backgroundSize: '300% 300%', animation: 'neuGradient 4s ease infinite' }}>
          <div className="neu-anim text-6xl inline-block" style={{ animation: 'neuSchweben 1.6s ease-in-out infinite' }} aria-hidden>🎉📦🎊</div>
          <h2 className="mt-1 text-lg font-bold leading-snug drop-shadow">{(SPRUECHE[spruch] ?? SPRUECHE[0])?.(name)}</h2>
        </div>
        <ul className="px-6 py-4 space-y-1.5 text-sm text-slate-700">
          {zeigen.map(z => <li key={z.key} data-testid="neu-popup-zeile">{z.text}</li>)}
          {zeilen.length > zeigen.length && <li className="text-slate-400">… und {zeilen.length - zeigen.length} weitere</li>}
        </ul>
        <div className="px-6 pb-5 flex gap-2">
          <button data-testid="neu-popup-zeigen"
                  onClick={() => { const ziel = zeilen[0]?.ziel ?? '/einkauf'; setZeilen([]); navigate(ziel); }}
                  className="flex-1 bg-brand text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:opacity-90">
            Zeig her! 👀
          </button>
          <button data-testid="neu-popup-spaeter" onClick={() => setZeilen([])}
                  className="border border-slate-300 text-slate-600 rounded-lg px-4 py-2.5 text-sm hover:bg-slate-50">
            Später
          </button>
        </div>
        <div className="px-6 pb-3 -mt-2 text-right">
          <button data-testid="neu-popup-ton" className="text-[11px] text-slate-400 hover:text-slate-600"
                  onClick={() => { const n = !ton; setTon(n); try { localStorage.setItem(TON_KEY, n ? '0' : '1'); } catch { /* egal */ } }}>
            {ton ? '🔔 Ton an' : '🔕 Ton aus'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Konfetti-Show auf einem Canvas (ohne Bibliothek): zwei Kanonen von unten, Regen von oben, Emojis; liefert die Aufräum-Funktion. */
export function konfetti(): () => void {
  try {
    if (typeof document === 'undefined') return () => {};
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return () => {};
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext?.('2d');
    if (!ctx) return () => {};
    canvas.setAttribute('data-testid', 'konfetti');
    canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:90';
    const w = (canvas.width = window.innerWidth);
    const h = (canvas.height = window.innerHeight);
    document.body.appendChild(canvas);
    const farben = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#facc15', '#14b8a6'];
    const emojis = ['🎉', '🎊', '📦', '⭐', '🛒', '✨'];
    type Teil = { x: number; y: number; vx: number; vy: number; s: number; r: number; vr: number; c: string; e?: string; g: number; leben: number };
    const teile: Teil[] = [];
    const rnd = (a: number, b: number) => a + Math.random() * (b - a);
    const farbe = () => farben[Math.floor(Math.random() * farben.length)] ?? '#3b82f6';
    // Kanone: Salve schräg nach oben (winkel in Grad, 0 = rechts, 90 = hoch)
    const kanone = (x: number, winkelMitte: number, n: number, verzug: number) => {
      for (let i = 0; i < n; i++) {
        const a = (winkelMitte + rnd(-28, 28)) * Math.PI / 180; const v = rnd(12, 26);
        teile.push({ x, y: h + 10, vx: Math.cos(a) * v, vy: -Math.sin(a) * v, s: rnd(7, 13), r: rnd(0, 6), vr: rnd(-0.3, 0.3), c: farbe(),
                     e: Math.random() < 0.12 ? emojis[Math.floor(Math.random() * emojis.length)] : undefined, g: 0.32, leben: -verzug });
      }
    };
    kanone(0, 62, 90, 0); kanone(w, 118, 90, 0);          // erste Salve sofort
    kanone(0, 55, 70, 25); kanone(w, 125, 70, 25);         // zweite Salve
    kanone(w * 0.5, 90, 80, 55);                           // dritte aus der Mitte
    for (let i = 0; i < 120; i++)                          // Regen von oben
      teile.push({ x: rnd(0, w), y: rnd(-h * 0.8, -10), vx: rnd(-1.5, 1.5), vy: rnd(2, 5), s: rnd(6, 12), r: rnd(0, 6), vr: rnd(-0.25, 0.25), c: farbe(),
                   e: Math.random() < 0.08 ? emojis[Math.floor(Math.random() * emojis.length)] : undefined, g: 0.05, leben: -rnd(20, 90) });
    const ende = Date.now() + 6500;
    let raf = 0; let frame = 0;
    const takt = () => {
      frame++;
      ctx.clearRect(0, 0, w, h);
      for (const t of teile) {
        t.leben++;
        if (t.leben < 0) continue;
        t.x += t.vx; t.y += t.vy; t.vy += t.g; t.vx *= 0.992; t.r += t.vr;
        ctx.save(); ctx.translate(t.x, t.y); ctx.rotate(t.r);
        if (t.e) { ctx.font = `${t.s * 2.2}px serif`; ctx.fillText(t.e, -t.s, t.s); }
        else { ctx.fillStyle = t.c; ctx.fillRect(-t.s / 2, -t.s / 4, t.s, t.s / 2); }
        ctx.restore();
      }
      if (Date.now() < ende && frame < 900) raf = requestAnimationFrame(takt); else canvas.remove();
    };
    raf = requestAnimationFrame(takt);
    return () => { cancelAnimationFrame(raf); canvas.remove(); };
  } catch { return () => {}; }
}
